import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useDispatch, useSelector } from 'react-redux';
import {
  resetOperation,
  setCommandMessage,
} from '../../../redux/operationSlice';
import { createSupplyPoint } from '../../../factory/symbols/createSupplyPoint';
import { addComponent } from '../../../redux/simpleSupplySlice';
import { generateUniqueId } from '../../../utils/generateUniqueId';
import { ComponentFactory } from '../../../factories/ComponentFactory';
import { findClosestLine, closestPointOnLineSegment } from '../../../shared/geometry/geometryUtils';
import { getMouseWorldPosition } from '../../../utils/getMouseWorldPosition';
import { BoxCommand } from '../../../modules/undoRedo';
// selectAllFloorPlans ve attachUsingDirectionToGroup KALDIRILDI - Box kaybolma fix

/**
 * Supply Box Drawing Hook (Chrono Undo/Redo destekli, anlık sahne eklemeli)
 */
export const useSupplyBoxDrawing = (scene, camera, renderer, undoRedoManager) => {
  const previewPanelRef = useRef(null);
  const dispatch = useDispatch();
  const { commandType } = useSelector((state) => state.operation);
  // Supply box etiketi: proje oluşturucuda dağıtım firması seçildiyse firma adı,
  // seçilmediyse "Şebeke". (elektrikDagitimName metadata'ya proje oluşturucuda yazılır.)
  const distributionName = useSelector((s) => s.project?.projectMetadata?.elektrikDagitimName);
  const supplyLabel = (distributionName && distributionName.trim()) || 'Şebeke';
  // floorPlans ve floorPlanGroup KALDIRILDI - Box kaybolma fix

  useEffect(() => {
    if (commandType !== 'drawSupplyPoint' || !scene || !camera || !renderer) return;
    dispatch(setCommandMessage('✅ SupplyPoint: yerleştirme noktasını seçin'));

    const domElement = renderer.domElement;

    const handleMouseMove = (event) => {
      let mousePos = getMouseWorldPosition(event, domElement, camera);
      const closestLine = findClosestLine(scene, mousePos);
      let angleRadians = 0;

      if (closestLine) {
        const { start, end } = closestLine;
        const direction = end.clone().sub(start).normalize();
        angleRadians = Math.atan2(direction.y, direction.x);
        mousePos = closestPointOnLineSegment(mousePos, start, end);
      }

      if (previewPanelRef.current) {
        previewPanelRef.current.position.copy(mousePos);
        previewPanelRef.current.rotation.z = angleRadians;
      } else {
        const supplyGroup = createSupplyPoint(scene, mousePos, supplyLabel);
        supplyGroup.rotation.z = angleRadians;
        supplyGroup.userData.isPreview = true;
        previewPanelRef.current = supplyGroup;
        scene.add(supplyGroup);
      }
    };

const handleMouseClick = (event) => {
  if (event.button !== 0) return;

  let mousePos = getMouseWorldPosition(event, domElement, camera);
  const closestLine = findClosestLine(scene, mousePos);
  let angleRadians = 0;

  if (closestLine) {
    const { start, end } = closestLine;
    const direction = end.clone().sub(start).normalize();
    angleRadians = Math.atan2(direction.y, direction.x);
    mousePos = closestPointOnLineSegment(mousePos, start, end);
  }

  // ✅ Component oluştur
  const componentId = generateUniqueId("supply");
  const newComponent = ComponentFactory.createComponent('supply', {
    id: componentId,
    name: supplyLabel,
    position: { x: mousePos.x, y: mousePos.y, z: 0 },
    rotation: { x: 0, y: 0, z: angleRadians },
  });

  // ✅ Sahneye hemen ekle (floorPlanGroup'a değil, direkt scene'e) - Box kaybolma fix
  const supplyGroup = createSupplyPoint(scene, mousePos, supplyLabel, componentId);
  supplyGroup.rotation.z = angleRadians;
  supplyGroup.userData.id = componentId;
  scene.add(supplyGroup);

  // ✅ Redux
  dispatch(addComponent(newComponent));

  // ✅ Chrono Undo/Redo
  if (undoRedoManager) {
    const tx = undoRedoManager.beginTransaction('Add Supply Box');
    const cmd = new BoxCommand('ADD', newComponent, {
      position: [mousePos.x, mousePos.y, 0],
      rotation: angleRadians,
      // floorPlanGroup KALDIRILDI - Box kaybolma fix
    }, {
      scene,
      dispatch,
    });
    undoRedoManager.addCommand(cmd);
    undoRedoManager.commitTransaction(tx);
  }

  // ✅ Preview temizle
  if (previewPanelRef.current) {
    scene.remove(previewPanelRef.current);
    previewPanelRef.current = null;
  }

  // ✅ Komutu sonlandır → kullanıcı yeni box çizemeyecek
  dispatch(resetOperation());
  dispatch(setCommandMessage('✅ SupplyPoint yerleştirildi (Ctrl+Z ile geri alınabilir)'));
};


    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        if (previewPanelRef.current) {
          scene.remove(previewPanelRef.current);
          previewPanelRef.current = null;
        }
        dispatch(resetOperation());
      }
    };

    domElement.addEventListener('mousemove', handleMouseMove);
    domElement.addEventListener('click', handleMouseClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      if (previewPanelRef.current) {
        scene.remove(previewPanelRef.current);
        previewPanelRef.current = null;
      }
      domElement.removeEventListener('mousemove', handleMouseMove);
      domElement.removeEventListener('click', handleMouseClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [commandType, scene, camera, renderer, dispatch, undoRedoManager, supplyLabel]); // floorPlanGroup KALDIRILDI - Box kaybolma fix
};
